import * as React from 'react';
import { useHistory, useParams } from 'react-router';
import { useQuery } from '../../core/hooks/useQuery';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHomeAppointments } from './AppointmentListActions';
import { RootState } from '../../core/store';
import { Breadcrumb, ConfigProvider, Table, TableProps } from 'antd';
import { customizeRenderEmpty, formatAddress } from '../../core/util/helpers';
import { Appointment } from '../../core/types';
import { fetchHome, fetchHomeContacts, searchHomes } from '../home/HomeActions';
import { ColumnProps } from 'antd/es/table';
import { formattedDateForTimezone } from '../../core/util/timezone';
import './AppointmentList.scss';
import { NavLink } from 'react-router-dom';
import { getSelectedHomeWithUsers } from 'features/home/HomeSlice';

interface AppointmentListParams {
  homeid?: string | undefined;
}

const columns = (timezone: string): ColumnProps<any>[] => {
  return [
    {
      key: 'id',
      title: 'Name',
      render: (record: Appointment) => <div>{record.name}</div>
    },
    {
      key: 'completed_tasks',
      title: 'Completed Tasks',
      render: (record: Appointment) => <div>{record.completed_tasks}</div>
    },
    {
      key: 'scheduled',
      title: 'Scheduled',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
      render: (record: Appointment) => (
        <div>{formattedDateForTimezone(record.scheduled, timezone)}</div>
      )
    },
    {
      key: 'completed',
      title: 'Completed',
      render: (record: Appointment) => (
        <div>
          {!!record.finished
            ? formattedDateForTimezone(record.finished, timezone)
            : '-'}
        </div>
      )
    }
  ];
};

const AppointmentList: React.FC = () => {
  const { homeid } = useParams<AppointmentListParams>();
  const selectedHome = useSelector(getSelectedHomeWithUsers);
  const { pagedHomeList } = useSelector((state: RootState) => state.home);
  const { pagedAppointmentList, loading } = useSelector(
    (state: RootState) => state.appointmentList
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const currentQueryParams = useQuery();

  const updateQueryParams = (param: any) => {
    const newParams = new URLSearchParams(currentQueryParams);
    for (const [key, value] of Object.entries(param)) {
      const paramName: any = key;
      const paramValue: any = value;
      newParams.set(paramName, paramValue);
    }
    history.replace({
      search: newParams.toString()
    });
  };

  const handleTableChange: TableProps<any>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    const tableSort: any = { ...sorter };
    let columnName: string;
    switch (tableSort.column.title) {
      case 'Name':
        columnName = 'id';
        break;
      case 'Scheduled':
        columnName = 'scheduled';
        break;
      case 'Completed':
        columnName = 'completed';
        break;
      default:
        columnName = 'scheduled';
    }
    const sortParams = {
      page: pagination.current,
      sort: columnName,
      order: tableSort.order === 'ascend' ? 'asc' : 'desc'
    };
    updateQueryParams(sortParams);
  };

  const selectAppointment = (appt: Appointment) => {
    history.push(`/home/${homeid}/appointments/${appt.id}`);
  };

  const fetchAppointments = (params: any) => {
    if (!!homeid) {
      dispatch(fetchHomeAppointments(homeid, params));
    }
  };

  useEffect(() => {
    dispatch(fetchHome(Number(homeid)));
    dispatch(fetchHomeContacts(Number(homeid)));
    /**
     * Only fetch home list if we don't already have the current home in pagedHomeList
     * (This is needed to display the homeowners associated with the home)
     */
    const currentHome = pagedHomeList?.items?.find(
      (home) => home.id.toString() === homeid
    );
    if (!currentHome) {
      dispatch(searchHomes({ page: 1, term: homeid }));
    }
    if (!!homeid && !pagedAppointmentList) {
      const defaultQueryParams = {
        sort: 'scheduled',
        order: 'asc',
        page: '1'
      };
      history.replace({
        search: new URLSearchParams(defaultQueryParams).toString()
      });
      fetchAppointments(defaultQueryParams);
    }
  }, []);

  useEffect(() => {
    const params: any = {
      sort: currentQueryParams.get('sort'),
      order: currentQueryParams.get('order'),
      page: currentQueryParams.get('page')
    };
    fetchAppointments(params);
  }, [currentQueryParams]);

  return (
    <div className="hk-appointments">
      <Breadcrumb className="hk-appointments-breadcrumbs" separator=">">
        <Breadcrumb.Item>
          <NavLink to={'/homes'}>Homes</NavLink>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Appointments</Breadcrumb.Item>
      </Breadcrumb>

      <h1>Appointments</h1>
      <div className="hk-appointments-title">
        {selectedHome && <span>{formatAddress(selectedHome)}</span>}
        <p>
          <>
            {selectedHome?.users?.map(
              (user, index) =>
                `${user}${index !== (selectedHome?.users?.length ?? 0) - 1 ? ', ' : ' - '}`
            )}
          </>
          <>ID: {selectedHome?.id}</>
        </p>
      </div>
      <ConfigProvider renderEmpty={customizeRenderEmpty}>
        <Table
          rowKey={(record) => record.id}
          rowClassName="hk-table-row"
          loading={{ spinning: loading, size: 'large' }}
          columns={columns(selectedHome?.market.timezone || '')}
          dataSource={pagedAppointmentList?.items || []}
          onChange={handleTableChange}
          pagination={{
            defaultCurrent: 1,
            total: pagedAppointmentList?.total,
            pageSize: pagedAppointmentList?.per_page,
            showSizeChanger: false
          }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                selectAppointment(record);
              }
            };
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default AppointmentList;
